import {useEffect, useMemo, useRef, useState} from 'react'
import {Calendar, DatePicker} from '@material-ui/pickers'
import {format} from 'date-fns'
import {useTranslation} from 'react-i18next'

import CompactDateSelector from './CompactDateSelector'
import ImageCalendarDay from './ImageCalendarDay'
import {useViewImageContext} from './ViewImageContext'
import {useViewLandContext} from '../ViewLandContext'
import {useIsMobile} from '../../../../../../shared/responsive'
import {scrollElementIntoMobilePanel} from '../../../../../../shared/scrollIntoMobilePanel'
import {REMOTE_SENSING_DATE_STATES as STATES, canPrepareRemoteSensingDate} from './remoteSensingDateModel'
import {normalizeRemoteSensingSource} from './remoteSensingModel'
import {
  buildPlanetHdCalendarModel, getPlanetHdAdjacentDates, getPlanetHdDayCloudState, hasKnownCloudPercent,
  isPlanetHdNavigable, planetHdItemKey, selectPlanetHdDayItem,
} from './planetHdCalendarModel'
import {isPlanetHdDateAllowed} from './planetHdAcquisitionQuery'

const displayDate = (date) => format(new Date(`${date}T00:00:00`), 'dd/MM/yyyy')
const HD_PREPARATION_POLL_MS = 15000

const PlanetHdDateSelector = ({openSignal = 0, onOpenSignalHandled, onPreparingChange}) => {
  const {t} = useTranslation()
  const context = useViewImageContext()
  const {land} = useViewLandContext()
  const isMobile = useIsMobile()
  const activeImage = context.getActiveLandImage(land.land_id)
  const {changeRemoteSensingAcquisition, ensureLandHdAcquisitions, getLandHdAcquisitionsState, prepareLandHdAcquisition} = context
  const source = useMemo(() => normalizeRemoteSensingSource(activeImage.imageType), [activeImage.imageType])
  // In-flight preparation intents remain bound to the visualization that
  // initiated them; the selected acquisition/date lives in shared land state.
  const scope = `${land.company_id || ''}:${land.land_id}:${activeImage.imageType}`
  const scopeRef = useRef(scope)
  scopeRef.current = scope
  const [cloudinessFilter, setCloudinessFilter] = useState(20)
  const [debouncedCloudinessFilter, setDebouncedCloudinessFilter] = useState(20)
  const [inspection, setInspection] = useState(null)
  const [openedDatePicker, setOpenedDatePicker] = useState(false)
  const [openedMobileCalendar, setOpenedMobileCalendar] = useState(false)
  const [preparation, setPreparation] = useState(null)
  const [preparationError, setPreparationError] = useState(null)
  const preparationLock = useRef(null)
  const pendingSelection = useRef(null)
  const lastHandledOpenSignal = useRef(0)
  const mobileCalendarRef = useRef(null)
  // Null requests the same bounded catalog without a provider cloud threshold.
  // Cloud changes below are local: cloudy/unknown days must remain inspectable.
  const acquisitionsState = getLandHdAcquisitionsState(land, source, null)
  const items = useMemo(() => (acquisitionsState.items || []).filter(
    (item) => isPlanetHdDateAllowed(item?.date)
  ), [acquisitionsState.items])
  const {itemsByDate, availableDates, navigableDates} = useMemo(
    () => buildPlanetHdCalendarModel(items, debouncedCloudinessFilter), [items, debouncedCloudinessFilter]
  )
  const activeItem = activeImage.selectedAcquisitionId && items.find((item) => item.acquisitionId === activeImage.selectedAcquisitionId)
  const selectionDate = inspection?.scope === scope ? inspection.date : activeItem?.date ||
    (activeImage.selectedDate ? format(activeImage.selectedDate, 'yyyy-MM-dd') : null)
  const inspectedDate = selectionDate || availableDates[0]
  const dayItems = itemsByDate.get(inspectedDate) || []
  const selectedItem = selectPlanetHdDayItem(dayItems, inspection?.scope === scope ? inspection.id : activeImage.selectedAcquisitionId)
  const {previousDate, nextDate} = getPlanetHdAdjacentDates(navigableDates, selectionDate)
  const calendarDate = inspectedDate ? new Date(`${inspectedDate}T00:00:00`) : new Date()
  const isLoadingDates = acquisitionsState.status === 'loading'
  const hasAvailableDates = availableDates.length > 0
  const isCalendarOpen = isMobile ? openedMobileCalendar : openedDatePicker
  const pendingPreparation = preparation?.scope === scope && preparation
  const isPreparing = Boolean(pendingPreparation)

  useEffect(() => {
    onPreparingChange?.(isPreparing)
  }, [isPreparing, onPreparingChange])

  useEffect(() => () => onPreparingChange?.(false), [onPreparingChange])
  const pendingItem = pendingPreparation && items.find((item) => planetHdItemKey(item) === pendingPreparation.id)
  const hasPreparingItems = items.some((item) => item.status === STATES.PREPARING)

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedCloudinessFilter(cloudinessFilter), 800)
    return () => window.clearTimeout(timer)
  }, [cloudinessFilter])

  useEffect(() => {
    ensureLandHdAcquisitions(land, source, null).catch(() => {})
  }, [ensureLandHdAcquisitions, land, source])

  useEffect(() => {
    if (acquisitionsState.status === 'error' || (!hasPreparingItems && !isPreparing)) return undefined
    const timer = window.setInterval(() => {
      ensureLandHdAcquisitions(land, source, null, {force: true}).catch(() => {})
    }, HD_PREPARATION_POLL_MS)
    return () => window.clearInterval(timer)
  }, [acquisitionsState.status, ensureLandHdAcquisitions, hasPreparingItems, isPreparing, land, source])

  useEffect(() => {
    if (!pendingItem || [STATES.CANDIDATE, STATES.PREPARING].includes(pendingItem.status)) return
    const intent = pendingSelection.current
    pendingSelection.current = null
    setPreparation(null)
    if (intent?.scope === scope && intent.id === planetHdItemKey(pendingItem) &&
        [STATES.READY, STATES.REJECTED_QUALITY].includes(pendingItem.status)) {
      setPreparationError(null)
      changeRemoteSensingAcquisition(land, pendingItem, source)
    }
  }, [pendingItem, scope, changeRemoteSensingAcquisition, land, source])

  useEffect(() => {
    setOpenedDatePicker(false)
    setOpenedMobileCalendar(false)
    setPreparationError(null)
    pendingSelection.current = null
    preparationLock.current = null
    setPreparation(null)
  }, [scope])

  useEffect(() => () => {
    pendingSelection.current = null
    preparationLock.current = null
  }, [])

  useEffect(() => {
    if (openSignal <= lastHandledOpenSignal.current || context.loading || isLoadingDates || isPreparing || !hasAvailableDates) return
    lastHandledOpenSignal.current = openSignal
    onOpenSignalHandled?.(openSignal)
    if (isMobile) setOpenedMobileCalendar(true)
    else setOpenedDatePicker(true)
  }, [
    openSignal,
    context.loading,
    isLoadingDates,
    isPreparing,
    hasAvailableDates,
    isMobile,
    onOpenSignalHandled,
  ])

  useEffect(() => {
    if (!isMobile || !openedMobileCalendar) return undefined
    return scrollElementIntoMobilePanel(mobileCalendarRef.current, {delay: 160})
  }, [isMobile, openedMobileCalendar, availableDates.length])

  const inspectItem = (item) => {
    if (!item || !isPlanetHdDateAllowed(item.date) || context.loading || isLoadingDates || isPreparing || preparationLock.current) return
    setInspection({scope, date: item.date, id: planetHdItemKey(item)})
    setPreparationError(null)
    // Only an explicit date/arrow/alternative selection may request an image.
    // Opening the calendar, changing cloud threshold and polling never prepare.
    pendingSelection.current = null
    if ([STATES.READY, STATES.REJECTED_QUALITY].includes(item.status)) {
      changeRemoteSensingAcquisition(land, item, source)
    } else {
      // Never offer zoning on the previous image while a different date loads.
      context.changeDate(land, null)
      if ([STATES.CANDIDATE, STATES.PREPARING].includes(item.status)) {
        pendingSelection.current = {scope, id: planetHdItemKey(item)}
        if (canPrepareRemoteSensingDate(item)) prepareSelectedItem(item)
        else setPreparation({scope, id: planetHdItemKey(item)})
      }
    }
  }
  const selectDate = (date) => {
    const bucket = itemsByDate.get(format(date, 'yyyy-MM-dd'))
    inspectItem(getPlanetHdDayCloudState(bucket, debouncedCloudinessFilter).representative)
    setOpenedDatePicker(false)
    setOpenedMobileCalendar(false)
  }
  const navigateDate = (date) => {
    if (context.loading || isLoadingDates) return
    const bucket = itemsByDate.get(date)?.filter((item) => isPlanetHdNavigable(item, debouncedCloudinessFilter))
    inspectItem(selectPlanetHdDayItem(bucket, planetHdItemKey(selectedItem)))
  }
  const prepareSelectedItem = async (item) => {
    if (!canPrepareRemoteSensingDate(item) || preparationLock.current || isPreparing) return
    const request = {}
    preparationLock.current = request
    const requestedScope = scope
    setPreparation({scope, id: planetHdItemKey(item)})
    setPreparationError(null)
    let accepted = false
    try {
      const result = await prepareLandHdAcquisition(land, item, source)
      if (preparationLock.current !== request || scopeRef.current !== requestedScope) return
      if (!result) throw new Error('preparation_not_accepted')
      accepted = true
      await ensureLandHdAcquisitions(land, source, null, {force: true})
    } catch (_) {
      if (scopeRef.current === requestedScope && preparationLock.current === request) {
        if (!accepted) {
          setPreparation(null)
          pendingSelection.current = null
        }
        setPreparationError(accepted
          ? t('images:hd_dates.load_refresh_error', {defaultValue: 'La imagen fue solicitada, pero no se pudo actualizar. Reintentá la consulta.'})
          : t('images:hd_dates.load_error', {defaultValue: 'No se pudo cargar la imagen. Volvé a elegir la fecha para reintentar.'}))
      }
    } finally {
      if (preparationLock.current === request) preparationLock.current = null
    }
  }
  const cloudLabel = (item) => hasKnownCloudPercent(item)
    ? t('images:hd_dates.cloud_percent', {percent: item.cloudPercent.toFixed(1), defaultValue: `${item.cloudPercent.toFixed(1)}% nubes`})
    : t('images:hd_dates.unknown_cloud', {defaultValue: 'Nubosidad desconocida'})
  const cloudStatus = (item) => !hasKnownCloudPercent(item)
    ? cloudLabel(item)
    : t(item.cloudPercent <= debouncedCloudinessFilter ? 'images:date_status.clear' : 'images:date_status.cloudy')
  const renderCalendarDay = (day, pickerDate, inCurrentMonth, dayComponent) => {
    if (!inCurrentMonth) return dayComponent
    const date = format(day, 'yyyy-MM-dd')
    const bucket = itemsByDate.get(date) || []
    const {cloudState, representative: item} = getPlanetHdDayCloudState(bucket, debouncedCloudinessFilter)
    const description = item ? cloudLabel(item)
      : t('images:hd_dates.no_image', {defaultValue: 'Sin imagen'})
    return <ImageCalendarDay inCurrentMonth={inCurrentMonth} cloudState={item ? cloudState : null}
      active={date === inspectedDate} title={description} ariaLabel={`${displayDate(date)} · ${description}`}>
      {dayComponent}
    </ImageCalendarDay>
  }
  const shouldDisableDate = (date) => !itemsByDate.has(format(date, 'yyyy-MM-dd'))
  const triggerLabel = isLoadingDates ? t('images:dates_loading')
    : selectionDate ? `${displayDate(selectionDate)}${selectedItem ? ` · ${cloudStatus(selectedItem)}` : ''}` : t('images:date_selector')
  const statusMessage = acquisitionsState.status === 'empty' ? t('images:hd_dates.empty', {defaultValue: 'No hay adquisiciones Planet para este filtro.'})
      : acquisitionsState.status === 'error' ? items.length > 0
        ? t('images:hd_dates.refresh_error', {defaultValue: 'No se pudieron actualizar las adquisiciones HD. Se muestran las últimas fechas obtenidas.'})
        : t('images:hd_dates.error', {defaultValue: 'No se pudieron consultar las adquisiciones HD.'}) : null

  return <div className="view-land-hd-calendar">
    <CompactDateSelector canGoPrev={Boolean(previousDate) && !context.loading && !isLoadingDates && !isPreparing}
      canGoNext={Boolean(nextDate) && !context.loading && !isLoadingDates && !isPreparing}
      onPrev={() => navigateDate(previousDate)} onNext={() => navigateDate(nextDate)}
      onOpen={() => isMobile ? setOpenedMobileCalendar((open) => !open) : setOpenedDatePicker(true)}
      disabled={context.loading || isLoadingDates || isPreparing || !hasAvailableDates}
      isCalendarOpen={isCalendarOpen} isLoadingDates={isLoadingDates} triggerLabel={triggerLabel}
      cloudinessFilter={cloudinessFilter} onCloudinessChange={setCloudinessFilter} />
    {isMobile && openedMobileCalendar && hasAvailableDates && <div ref={mobileCalendarRef} className="view-land-date-selector__inline-calendar">
      <Calendar date={calendarDate} onChange={selectDate} renderDay={renderCalendarDay}
        shouldDisableDate={shouldDisableDate} allowKeyboardControl={false} />
    </div>}
    {!isMobile && <DatePicker open={openedDatePicker} value={calendarDate} autoOk okLabel={null}
      cancelLabel={t('common:actions.cancel')} renderDay={renderCalendarDay} shouldDisableDate={shouldDisableDate}
      onChange={selectDate} onOpen={() => setOpenedDatePicker(true)} onClose={() => setOpenedDatePicker(false)}
      animateYearScrolling DialogProps={{maxWidth: false, PaperProps: {className: 'view-land-date-picker-dialog'}}}
      TextFieldComponent={() => null} />}
    {statusMessage && <div className="view-land-hd-calendar__status" role={acquisitionsState.status === 'error' ? 'alert' : 'status'}>{statusMessage}</div>}
    {isPreparing && !preparationError && <div className="view-land-hd-calendar__status" role="status">
      {t('images:hd_dates.preparing_wait')}
    </div>}
    {(acquisitionsState.status === 'error' || (isPreparing && preparationError)) && <button type="button" className="btn btn-sm btn-light"
      onClick={() => ensureLandHdAcquisitions(land, source, null, {force: true}).catch(() => {})}>
      {t('images:hd_dates.retry', {defaultValue: 'Reintentar'})}
    </button>}
    {selectedItem && (preparationError ||
      [STATES.REJECTED_QUALITY, STATES.REJECTED, STATES.FAILED].includes(selectedItem.status)) && <div className="view-land-hd-calendar__detail">
      {selectedItem.status === STATES.REJECTED_QUALITY && <>
        <small role="alert">{selectedItem.qualityWarning || selectedItem.reason || t('images:hd_date_state.rejected_quality_warning', {defaultValue: 'Visualizable con advertencia; no apta para ambientar.'})}</small>
      </>}
      {[STATES.REJECTED, STATES.FAILED].includes(selectedItem.status) && <small role="alert">
        {t('images:hd_dates.image_unavailable', {defaultValue: 'No se pudo cargar esta imagen. Elegí otra fecha.'})}
      </small>}
      {preparationError && <small role="alert">{preparationError}</small>}
    </div>}
  </div>
}

export default PlanetHdDateSelector
